import { useEffect, useState } from 'react'
import { useAppDispatch } from '../../reducers/store'
import { loginUser, lookupOrganization, selectUser, setLoginPage } from '../../reducers/users'
import { useDispatch, useSelector } from 'react-redux'
import { Button } from '@radix-ui/themes'
import { useNavigate, useLocation } from 'react-router-dom'
import toast from 'react-hot-toast'
import { Input } from '../input/Input'
import { setCurrentPage as setSettingsPage } from '../../reducers/settings'
import { RoleType, SessionState } from '../../types/UserTypes'
import { getRoles } from '../../utils/roles'
import { DEFAULT_TOAST_CONFIG, SHORT_TOAST_CONFIG } from '../toast/consts'

function LoginForm() {
  const [validEmail, setValidEmail] = useState<boolean>(false)
  const [email, setEmail] = useState<string>('')
  const [password, setPassword] = useState<string>('')
  const dispatch = useDispatch()
  const di = useAppDispatch()
  const navigate = useNavigate()
  const { hasOrgSso } = useSelector(selectUser)
  const location = useLocation()

  const handleLogin = async () => {
    if (email === '') {
      toast.error('Please enter your email', SHORT_TOAST_CONFIG)
      return
    } else if (password === '') {
      toast.error('Please enter your password', SHORT_TOAST_CONFIG)
      return
    } else {
      await di(loginUser(email, password)).then((res) => {
        if (res.error) {
          toast.error(res.error, DEFAULT_TOAST_CONFIG)
        } else {
          const roles = getRoles(res as SessionState) ?? []
          dispatch(
            setSettingsPage(
              roles.includes(RoleType.SUPER_ADMIN)
                ? 'ADMIN'
                : roles.includes(RoleType.PLATFORM_MGMT)
                  ? 'SSO'
                  : roles.includes(RoleType.ACCESS_MGMT)
                    ? 'CONDITIONAL_ACCESS'
                    : 'SETTINGS'
            )
          )
          const returnTo = getSafeReturnTo(location.search)
          if (returnTo) {
            if (returnTo.startsWith('http')) {
              // same-origin absolute url -> full redirect
              window.location.assign(returnTo)
            } else {
              // relative path -> SPA navigation
              navigate(returnTo, { replace: true })
            }
          } else {
            navigate('/dashboard', { replace: true })
          }
        }
      })
    }
  }

  const getSafeReturnTo = (search: string) => {
    const params = new URLSearchParams(search)
    const rt = params.get('returnTo')
    if (!rt) return null

    try {
      const url = new URL(rt)
      // Only allow same origin to avoid open-redirects
      if (url.origin === window.location.origin) return url.toString()
      return null
    } catch {
      // If it's a relative path instead of absolute URL, allow it
      if (rt.startsWith('/')) return rt
      return null
    }
  }

  const lookupByEmail = async () => {
    if (email === '' || !validEmail) {
      toast.error('Please enter a valid email address', SHORT_TOAST_CONFIG)
    } else {
      await di(lookupOrganization(email))
    }
  }

  useEffect(() => {
    const valid = email.match(/^([\w.%+-]+)@([\w-]+\.)+([\w]{2,})$/i)
    valid ? setValidEmail(true) : setValidEmail(false)
  }, [email])

  const handleButtonLogic = () => {
    hasOrgSso === undefined ? lookupByEmail() : handleLogin()
  }

  return (
    <form
      onSubmit={(e) => {
        e.preventDefault()
        handleButtonLogic()
      }}
      className="w-[80%] lg:w-[70%] 2xl:w-[60%] max-w-[460px]"
    >
      <p className="text-mainText text-3xl text-left mb-6">Sign in to iVerify EDR</p>
      <label className="text-mainText w-full">Email Address</label>
      <div className="mt-2">
        <Input autoFocus={true} value={email} type="email" placeholder="name@example.com" onChange={setEmail} />
      </div>
      {hasOrgSso === false && (
        <>
          <label className="text-mainText w-full block mt-6">Password</label>
          <div className="mt-2">
            <Input
              autoFocus={email !== ''}
              value={password}
              type="password"
              placeholder="password"
              onChange={setPassword}
            />
          </div>
        </>
      )}

      <Button className="w-full rounded mt-6 cursor-pointer bg-PurpleBlue-9 dark:bg-mainPurple hover:bg-PurpleBlue-10 dark:hover:bg-tertiaryPurple active:bg-PurpleBlue-10 dark:active:bg-tertiaryPurple">
        {hasOrgSso === undefined ? 'Continue' : 'Sign In'}
      </Button>

      {hasOrgSso === false && (
        <Button
          onClick={() => dispatch(setLoginPage('FORGOT'))}
          className="text-mainPurple dark:text-secondaryPurple mt-4 cursor-pointer text-center w-[96%] ml-0"
          variant="ghost"
        >
          Forgotten Password
        </Button>
      )}
    </form>
  )
}

export default LoginForm
